import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

/**
 * Pitch conversion: of everything ever pitched, where did it end up.
 *
 * Percentages, not counts, are the headline — "45% lost" is the number the
 * board discusses — but the count sits under each bar because a percentage of
 * an unstated base is not checkable.
 *
 * Colours carry meaning here rather than coming from the categorical palette:
 * won and lost are the whole point of the chart and reading them off a legend
 * would be a step backwards.
 */
const OUTCOME_COLORS = {
  won: "#6dcc09",
  lost: "#c2192b",
  "at contract": "#3facc6",
  "at pitch": "#cfa77b",
  rolled: "#7d8b96",
  other: "#cccccc",
};

export default function JourneyBarChart({
  groups = [],
  population,
  winRate,
  comparison = null,
  periodLabel = null,
  onGroupClick,
}) {
  if (!groups.length || !population) {
    return (
      <p className="py-12 text-center text-sm text-[#4a5b68]">
        No deals that went through a pitch have closed in this period.
      </p>
    );
  }

  const previous = Object.fromEntries(
    (comparison?.groups || []).map((group) => [group.label, group]),
  );
  const data = groups.map((group) => ({
    name: group.label,
    share: group.share ?? 0,
    count: group.count,
    lastShare: previous[group.label]?.share ?? 0,
    lastCount: previous[group.label]?.count ?? 0,
    fill: OUTCOME_COLORS[String(group.label).toLowerCase()] || OUTCOME_COLORS.other,
  }));
  const hasComparison = Boolean(comparison);

  const rate = (value) => (value === null || value === undefined ? "n/a" : `${value}%`);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <span className="text-[#4a5b68]">
          <span className="font-sans text-lg font-bold text-[#323c44]">{population}</span>{" "}
          deals won or lost
          {periodLabel && <span className="ml-1 text-[11px] text-[#7d8b96]">({periodLabel})</span>}
        </span>
        <span className="text-[#4a5b68]">
          Win rate{" "}
          <span className="rounded-full bg-[#eef8e2] px-2.5 py-0.5 font-semibold tabular-nums text-[#3f7a06]">
            {rate(winRate)}
          </span>
        </span>
        {hasComparison && (
          <span className="text-[#4a5b68]">
            Last year, same period{" "}
            <span className="rounded-full bg-[#efefef] px-2.5 py-0.5 font-semibold tabular-nums text-[#323c44]">
              {rate(comparison.win_rate)}
            </span>{" "}
            <span className="text-[11px] text-[#7d8b96]">of {comparison.population}</span>
          </span>
        )}
        {hasComparison && (
          <span className="text-[#4a5b68]">
            Whole of last year{" "}
            <span className="rounded-full bg-[#efefef] px-2.5 py-0.5 font-semibold tabular-nums text-[#323c44]">
              {rate(comparison.full_year_win_rate)}
            </span>{" "}
            <span className="text-[11px] text-[#7d8b96]">of {comparison.full_year_population}</span>
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={280} minWidth={260}>
        <BarChart data={data} margin={{ top: 24, right: 16, left: -8, bottom: 0 }} barGap={6}>
          <CartesianGrid stroke="#dddddd" vertical={false} />
          <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 12 }} />
          <YAxis
            tickLine={false}
            axisLine={false}
            tick={{ fill: "#4a5b68", fontSize: 12 }}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 100]}
          />
          <Tooltip
            cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
            contentStyle={{ borderRadius: 8, borderColor: "#cccccc", boxShadow: "0 0 20px rgba(0, 0, 0, 0.12)" }}
            formatter={(value, name, entry) => [
              name === "lastShare"
                ? `${value}%, ${entry?.payload?.lastCount} deals`
                : `${value}%, ${entry?.payload?.count} deals`,
              name === "lastShare" ? "Last year, same period" : "This year",
            ]}
          />
          <Bar
            dataKey="share"
            maxBarSize={72}
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
            cursor={onGroupClick ? "pointer" : undefined}
            onClick={(entry) => onGroupClick && onGroupClick(entry?.name ?? entry?.payload?.name)}
          >
            {data.map((entry) => (
              <Cell key={entry.name} fill={entry.fill} />
            ))}
            <LabelList
              dataKey="share"
              position="top"
              fill="#323c44"
              fontSize={13}
              fontWeight={700}
              formatter={(value) => `${value}%`}
            />
            <LabelList
              dataKey="count"
              position="insideBottom"
              fill="#ffffff"
              fontSize={11}
              fontWeight={600}
              offset={8}
            />
          </Bar>
          {hasComparison && (
            <Bar dataKey="lastShare" maxBarSize={48} radius={[4, 4, 0, 0]} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} fillOpacity={0.35} />
              ))}
              <LabelList
                dataKey="lastShare"
                position="top"
                fill="#7d8b96"
                fontSize={11}
                fontWeight={600}
                formatter={(value) => `${value}%`}
              />
            </Bar>
          )}
        </BarChart>
      </ResponsiveContainer>
      {hasComparison && (comparison.notes || []).map((note) => (
        <p key={note} className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
          {note}
        </p>
      ))}
      {hasComparison && (
        <p className="mt-1 text-[11px] text-[#7d8b96]">
          Solid bars are this year; faded bars are the same period last year ({comparison.period_label}).
        </p>
      )}
    </div>
  );
}
